import { useState, useCallback, useRef } from 'react';
import { prepareDocumentsForAI } from '../utils/documentChunking';

const stripUnsafeChars = (value, preserveLineBreaks = true) => {
  if (value == null) return '';

  return Array.from(String(value))
    .filter((ch) => {
      const code = ch.charCodeAt(0);

      // Remove Unicode line/paragraph separators that break JSON parsing.
      if (code === 0x2028 || code === 0x2029) return false;

      // Remove C1 control characters.
      if (code >= 0x7f && code <= 0x9f) return false;

      // Remove ASCII control chars, optionally preserving CR/LF.
      if (code <= 0x1f) {
        if (preserveLineBreaks && (code === 0x0a || code === 0x0d)) return true;
        return false;
      }

      return true;
    })
    .join('');
};

/**
 * Sanitize content for API submission to prevent JSON errors
 * @param {string} content - The content to sanitize
 * @returns {string} - Sanitized content
 */
const sanitizeApiContent = (content) => {
  if (!content) return '';
  
  try {
    // Basic sanitization to remove characters that could break JSON
    return stripUnsafeChars(content, true)
      // Preserve newlines but escape for JSON
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      // Use JSON.stringify for proper escaping, then remove the surrounding quotes
      .replace(/\\"/g, '"') // Fix any double escaped quotes first
      .replace(/"/g, '\\"'); // Then escape all quotes
  } catch (e) {
    console.error('Error sanitizing content:', e);
    return 'Error processing content'; // Safe fallback
  }
};

/**
 * Custom hook for chat functionality with AI integration
 * @param {Array} documents - Documents to analyze
 * @param {number} MAX_TOKENS - Maximum token limit
 */
const useChatWithAI = (documents, _, MAX_TOKENS) => {
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isAnalyzed, setIsAnalyzed] = useState(false);

  const cancelStreamRef = useRef(false);
  const abortControllerRef = useRef(null);
  const pendingStreamRef = useRef({ fullText: '', responseId: null });
  const streamingResponseRef = useRef('');
  const displayBufferRef = useRef('');
  const rafRef = useRef(null);

  const stopRafLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const startRafLoop = useCallback(() => {
    if (rafRef.current) return;
    const flush = () => {
      const buffered = displayBufferRef.current;
      setStreamingResponse(buffered);
      streamingResponseRef.current = buffered;
      rafRef.current = requestAnimationFrame(flush);
    };
    rafRef.current = requestAnimationFrame(flush);
  }, []);

  const completeStreamingResponse = useCallback((finalText, responseId) => {
    stopRafLoop();
    const safeText = typeof finalText === 'string' ? finalText :
                     (finalText ? String(finalText) : "No valid response received");
    setStreamingResponse(safeText);
    setIsWaitingForResponse(false);
    pendingStreamRef.current = { fullText: '', responseId: null };
    streamingResponseRef.current = '';
    displayBufferRef.current = '';

    setTimeout(() => {
      setChatMessages(prev => [...prev, {
        id: responseId,
        sender: 'ai',
        text: safeText
      }]);
      setIsStreaming(false);
      setStreamingResponse('');
    }, 50);
  }, [stopRafLoop]);

  const simulateStreamingResponse = useCallback((text, responseId) => {
    pendingStreamRef.current = { fullText: text, responseId };
    cancelStreamRef.current = false;
    displayBufferRef.current = '';

    let charIndex = 0;
    let lastTime = null;
    const charsPerSecond = 80;

    startRafLoop();

    const advance = (now) => {
      if (cancelStreamRef.current) return;
      if (lastTime === null) lastTime = now;

      const charsToAdd = Math.floor((now - lastTime) * charsPerSecond / 1000);
      if (charsToAdd > 0) {
        charIndex = Math.min(charIndex + charsToAdd, text.length);
        displayBufferRef.current = text.slice(0, charIndex);
        lastTime = now;
      }

      if (charIndex < text.length) {
        requestAnimationFrame(advance);
      } else {
        completeStreamingResponse(text, responseId);
      }
    };

    requestAnimationFrame(advance);
  }, [startRafLoop, completeStreamingResponse]);

  const interruptStreaming = useCallback(() => {
    cancelStreamRef.current = true;
    stopRafLoop();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const { fullText, responseId } = pendingStreamRef.current;
    const textToCommit = fullText || streamingResponseRef.current;

    if (textToCommit && responseId) {
      setChatMessages(prev => [...prev, { id: responseId, sender: 'ai', text: textToCommit }]);
    }

    setIsStreaming(false);
    setIsWaitingForResponse(false);
    setStreamingResponse('');
    streamingResponseRef.current = '';
    displayBufferRef.current = '';
    pendingStreamRef.current = { fullText: '', responseId: null };
  }, [stopRafLoop]);

  /**
   * Processes a chat message and sends it to the AI service
   */
  const sendToAI = useCallback((messageId, userInput) => {
    try {
      // Prepare conversation history for Azure OpenAI
      let systemMessage = `
        You are an expert system supporting the Institutional Review Board (IRB) at The University of Alabama at Birmingham (UAB). 

        Purpose:
        Analyze IRB submission materials (e.g., PORF,, IRB application, and study attachments) to identify pre-submission errors and compliance gaps. The goal is to help investigators correct issues before official IRB review, reducing Step 1 and Step 2 delays. Your job is to simply provide a report, not to ask followup questions or offer to do any work for them.

        Core Responsibilities:
        Review all submitted materials holistically.
        Detect incomplete, inconsistent, or noncompliant content.
        Cross-reference against required trainings, regulatory standards, and institutional policies.
        Recommend actionable, clear corrections.
        Produce a structured report organized by document type to streamline Admin Pre-Review letters.

        Key Definitions:
        IRB – Institutional Review Board overseeing human subjects research.
        PORF – Protocol Oversight Review Form; confirms scientific review by the investigator’s School or College.
        SRC – Scientific Review Committee; may satisfy PORF requirement when approved documentation is included.
        COI – Conflict of Interest; financial or personal interests that may bias research.
        CITI – Collaborative Institutional Training Initiative.
        HIPAA – Health Insurance Portability and Accountability Act.
        FERPA – Family Educational Rights and Privacy Act.
        GDPR – General Data Protection Regulation.
        CFR – Code of Federal Regulations.
        PI – Principal Investigator.
        PHI / PII – Protected or Personally Identifiable Information.

        Required Training Rules (supersede other assumptions):

        All investigators and key personnel must have ONE of the following current:
        IRB Training – Biomedical, or
        IRB Training – Social and Behavioral
        If either is expired, the corresponding refresher training (Biomedical or Social/Behavioral/Educational) is required.
        If the project is a clinical trial (as indicated in the ePortfolio):
        CITI Good Clinical Practice (GCP) training is required.
        If expired, the GCP Refresher Course is required.
        Recognize that refresher completion at submission resolves prior lapses.
        Do not flag unrelated or optional trainings.

        Relevant Regulations (summaries only; include links in output):
        45 CFR 46 – Protection of Human Subjects (Common Rule).
        45 CFR 46 Subparts B, C, D – Pregnant Women/Fetuses, Prisoners, and Children.
        21 CFR 50 and 56 – FDA Human Subjects and IRB regulations.
        21 CFR 312 and 812 – IND and IDE regulations.
        21 CFR 11 – Electronic records and signatures.
        42 CFR 11 – Clinical trial registration.
        45 CFR 160 and 164 – HIPAA privacy and security rules.

        Analysis Order and Logic:
        Determine whether the project meets the definition of research.
        If not, recommend “Not Human Subjects Research.”
        If yes, assess whether it involves human subjects.
        If yes, analyze whether it is exempt under the 2018 Common Rule.
        If likely exempt, STOP analysis after providing exemption rationale and citation.
        Only if not exempt, evaluate under expedited review criteria.
        If not expedited, note that it requires full board review.

        Cross-Document Consistency:
        Compare the protocol, consent forms, and all attachments for:
        Differences in stated procedures (e.g., surveys vs interviews).
        Conflicting waiver/authorization selections.
        Outdated or inconsistent start/end dates.
        Role discrepancies in Personnel section (e.g., missing PI or unchecked activity boxes).
        Consent forms describing groups or activities not listed elsewhere.
        All such inconsistencies should be flagged with pinpointed section or filename references.

        Document Completeness:
        Proactively detect missing or incomplete materials such as:
        Prospective data sheets, recruitment emails, referral lists, or instruments.
        Consent or information sheets.
        PORF signatures or incomplete SRC review documentation.
        Missing attachments or incomplete branching logic in the ePortfolio.
        Provide a checklist-style call-out listing missing or incomplete items.

        Section Detail Requirements:
        Always verify Risks and Privacy/Confidentiality.
        There must always be some risk statement and an explanation of privacy/confidentiality measures.
        Highlight vague or insufficient detail regarding methods, risks, or data handling.
        Suggest what additional information is needed for expedited or full-board standards.
        If narrative text is unclear or incomplete, flag as “Requires Investigator Clarification.”
        Note when participant groups (e.g., patients vs stakeholders) have different procedures not clearly distinguished.
        HIPAA / FERPA Analysis:
        Flag when the research uses PHI or involves HIPAA-covered entities.
        Flag when FERPA protections apply to educational records.
        Identify when IP addresses or identifiable metadata (e.g., in online surveys) are collected.
        Distinguish between information sheets and consent forms, and note whether HIPAA authorization applies.

        Output Organization:
        Title of the proposed project 
        Principal investigator 
        Protocol Number
        Study Overview
        Personnel and Training
        Funding and Sponsor
        Locations and Collaborations
        Study Design and Procedures
        Risks, Benefits, and Privacy
        Consent and Recruitment
        Data Management and Security
        Supporting Documents (PORF, attachments)
        Within each section, provide:
        Summary findings
        Critical Errors (must fix before submission)
        Corrective Recommendations (actionable guidance)
        Warnings (non-blocking but advised)
        Missing Materials checklist
        Courtesy reminders (upcoming expirations, incomplete signatures, training nearing renewal)
        Tone and Format:
        Use plain, professional language with bulleted lists.
        Maintain lay readability (~8th-grade level).
        Be directive but collegial.
        Identify ambiguous areas clearly and suggest corrective actions.
        Always cite specific regulations briefly with links.
        Keep output human-readable; do not include code or markup tags.
        Report should read like a pre-review memo, not a regulatory audit.
        Always provide a disclaimer at the end that this report is not exhaustive and the investigator should review all materials carefully before final submission.

        Quality Emphasis:
        Flag all incomplete PORFs, missing signatures, or unclear SRC substitutions.
        Identify missing checkboxes or incomplete activity roles in the Personnel section.
        Highlight incomplete sections or vague narratives.
        Provide contextual suggestions for improving clarity, readability, and compliance alignment.
        Maintain focus on consistency, completeness, and participant protection.
        Do not ask any follow-up questions or suggest further interactions (you are proving a report, not chatting).

        Appendix – Common Acronyms:
        AE, BAA, CITI, CFR, COI, EMR, FERPA, FWA, GCP, GDPR, HIPAA, HRPP, IND, IDE, IRB, NIH, PHI, PI, PII, PORF, PPRA, QI, SAE, SOP, SRC, UAB, UPIRSO.
      `;
      const formattedMessages = [
        { role: "system", content: systemMessage }
      ];
      
      // Add chat history
      chatMessages.forEach(msg => {
        const role = msg.sender === 'user' ? 'user' : 'assistant';
        formattedMessages.push({ role, content: msg.text });
      });
      
      // Add current user message
      formattedMessages.push({ role: 'user', content: userInput });
      
      // Add document context to the last user message
      if (formattedMessages.length > 0 && documents.length > 0) {
        const lastMessageIndex = formattedMessages.length - 1;
        if (formattedMessages[lastMessageIndex].role === 'user') {
          // Prepare documents with automatic chunking to fit within token limits
          // Reserve tokens for the system message and user query
          const systemPromptTokens = 500; // Approximate tokens for system prompt
          const userMsgTokens = 100; // Approximate tokens for user message
          
          // First, verify document contents
          console.log('Processing documents:', documents.map(doc => ({
            name: doc.name,
            contentPreview: doc.content.slice(0, 100) + '...',
            contentLength: doc.content.length
          })));
          
          // Prepare documents, chunking if needed
          const documentContext = prepareDocumentsForAI(
            documents, 
            MAX_TOKENS - 1000, // Reserve tokens for completion response
            systemPromptTokens,
            userMsgTokens
          );
          
          console.log('Document context after chunking:', 
            documentContext.slice(0, 200) + '... [truncated for log]',
            `(${documentContext.length} chars)`);
          
          // Append to user message
          formattedMessages[lastMessageIndex].content = 
            `Documents:\n${documentContext}\n\nUser question: ${formattedMessages[lastMessageIndex].content}`;
        }
      }
      
      // Prepare API payload - make sure all messages are properly formatted
      const safeMessages = formattedMessages.map(msg => ({
        role: msg.role,
        content: typeof msg.content === 'string' ? sanitizeApiContent(msg.content) : ''
      }));
      
      const payload = {
        messages: safeMessages,
        //temperature: 0.7,
        max_completion_tokens: MAX_TOKENS,
        frequency_penalty: 0,
        presence_penalty: 0,
        stream: true
      };
      
      // Log API submission details
      console.log('Sending to AI service:', {
        messageCount: payload.messages.length,
        firstMessagePreview: payload.messages[0].content.slice(0, 50) + '...',
        lastMessagePreview: payload.messages[payload.messages.length - 1].content.slice(0, 100) + '...',
        totalChars: payload.messages.reduce((acc, msg) => acc + msg.content.length, 0),
        settings: {
          temperature: payload.temperature,
          maxTokens: payload.max_tokens
        }
      });
      
      // Create new message ID for the response
      const responseId = Date.now();

      // Initialize streaming state
      setStreamingResponse('');
      streamingResponseRef.current = '';
      displayBufferRef.current = '';
      setIsStreaming(true);
      pendingStreamRef.current = { fullText: '', responseId };
      cancelStreamRef.current = false;
      abortControllerRef.current = new AbortController();
      startRafLoop();
      
      // Retrieve Azure OpenAI environment variables
      const apiKey = process.env.REACT_APP_AZURE_OPENAI_KEY;
      const endpoint = process.env.REACT_APP_AZURE_OPENAI_ENDPOINT;
      const deploymentName = process.env.REACT_APP_AZURE_OPENAI_DEPLOYMENT_NAME;
      const apiVersion = process.env.REACT_APP_AZURE_OPENAI_API_VERSION;

      const hasEnvVars = Boolean(apiKey && endpoint && deploymentName && apiVersion);
      
      if (hasEnvVars) {
        // Use actual API with environment variables
        const url = `${endpoint}/openai/deployments/${deploymentName}/chat/completions?api-version=${apiVersion}`;
        
        // Store complete response for when streaming is done
        let fullResponse = '';
        
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'api-key': apiKey
          },
          signal: abortControllerRef.current.signal,
          body: JSON.stringify(payload, (key, value) => {
            // Additional safety check for string values
            if (typeof value === 'string') {
              // Remove invisible/control characters while preserving newlines
              return stripUnsafeChars(value, true);
            }
            return value;
          })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error(`API call failed with status: ${response.status}`);
          }
          
          // Get reader for streaming response
          const reader = response.body.getReader();
          const decoder = new TextDecoder("utf-8");
          
          // Function to recursively read the stream
          function readStream() {
            return reader.read().then(({ done, value }) => {
              if (done) {
                // Streaming complete - add the full message to chat history
                completeStreamingResponse(fullResponse, responseId);
                return;
              }
              
              // Decode the chunk and process it
              const chunk = decoder.decode(value, { stream: true });
              
              try {
                // Process the chunk - format depends on Azure OpenAI API
                const lines = chunk.split('\n').filter(line => line.trim() !== '');
                
                lines.forEach(line => {
                  if (line.startsWith('data: ')) {
                    const data = line.substring(6);
                    if (data === '[DONE]') return;
                    
                    try {
                      // Sanitize the JSON data to ensure it's valid, but preserve newlines
                      const sanitizedData = stripUnsafeChars(data, true)
                                               .replace(/\\"/g, '"') // Fix any double escaped quotes
                                               .replace(/\\\\/g, '\\'); // Fix any double escaped backslashes
                      
                      const parsed = JSON.parse(sanitizedData);
                      // Get content and ensure it's a valid string
                      const content = parsed.choices?.[0]?.delta?.content || '';
                      
                      // Only process valid content
                      if (content && typeof content === 'string') {
                        const safeContent = stripUnsafeChars(content, true);
                        fullResponse += safeContent;
                        pendingStreamRef.current.fullText = fullResponse;
                        displayBufferRef.current = fullResponse;
                      }
                    } catch (e) {
                      console.error('Error parsing streaming data:', e, 'Raw data:', data);
                    }
                  }
                });
              } catch (e) {
                console.error('Error processing stream chunk:', e);
              }
              
              // Continue reading
              return readStream();
            });
          }
          
          // Start reading the stream
          return readStream();
        })
        .catch(error => {
          if (error.name === 'AbortError') return;
          console.error('Error calling Azure OpenAI:', error);

          // Reset states and show error message to user
          setIsStreaming(false);
          setIsWaitingForResponse(false);

          setChatMessages(prev => [...prev, {
            id: Date.now(),
            sender: 'ai',
            text: 'Sorry, I encountered an error analyzing your documents. Please try again.'
          }]);
        });
      } else {
        console.warn('Azure OpenAI environment variables not found. Using simulation mode.');
        setTimeout(() => {
          simulateStreamingResponse("Analyzing your documents... I can see that you've uploaded several files. Based on the content, I'd recommend focusing on the key points mentioned in the first document. The data indicates several important trends that might be relevant to your question.", responseId);
        }, 1000);
      }
    } catch (error) {
      console.error('Error in sendToAI:', error);
      
      // Reset states and show error message
      setIsStreaming(false);
      setIsWaitingForResponse(false);
      
      setChatMessages(prev => [...prev, { 
        id: Date.now(), 
        sender: 'ai', 
        text: 'An error occurred while processing your request. Please try again.' 
      }]);
    }
  }, [chatMessages, documents, simulateStreamingResponse, completeStreamingResponse, startRafLoop, MAX_TOKENS]);

  /**
   * Handles document analysis - initiates AI review without user message
   */
  const analyzeDocuments = useCallback(() => {
    if (documents.length === 0 || isWaitingForResponse) return;
    
    // Set waiting state
    setIsWaitingForResponse(true);
    
    const analysisId = Date.now();
    
    // Send to AI with special 'analyze' command
    setTimeout(() => {
      sendToAI(analysisId, 'Please analyze these documents and provide a comprehensive assessment.');
      setIsAnalyzed(true); // Mark as analyzed after sending
    }, 500);
  }, [documents, isWaitingForResponse, sendToAI]);

  const hydrateInitialReport = useCallback((reportText) => {
    if (typeof reportText !== 'string' || reportText.trim() === '') {
      throw new Error('Report text is required to hydrate chat history.');
    }

    const sanitizedReport = reportText.trim();
    stopRafLoop();
    setIsStreaming(false);
    setIsWaitingForResponse(false);
    setStreamingResponse('');
    pendingStreamRef.current = { fullText: '', responseId: null };
    streamingResponseRef.current = '';
    displayBufferRef.current = '';

    setChatMessages(prev => [
      {
        id: Date.now(),
        sender: 'ai',
        text: sanitizedReport
      },
      ...prev
    ]);
    setIsAnalyzed(true);
  }, [stopRafLoop]);

  /**
   * Handles form submission of a new message
   */
  const handleSubmit = useCallback((e) => {
    if (e) e.preventDefault();

    if (currentMessage.trim() === '') return;
    if (!isStreaming && isWaitingForResponse) return;

    if (isStreaming) interruptStreaming();

    const userMessageId = Date.now();
    const userInput = currentMessage;

    setChatMessages(prev => [...prev, {
      id: userMessageId,
      sender: 'user',
      text: userInput
    }]);

    setCurrentMessage('');
    setIsWaitingForResponse(true);

    setTimeout(() => {
      sendToAI(userMessageId, userInput);
    }, 500);
  }, [currentMessage, isWaitingForResponse, isStreaming, interruptStreaming, sendToAI]);

  return {
    chatMessages,
    currentMessage,
    setCurrentMessage,
    isWaitingForResponse,
    isStreaming,
    streamingResponse,
    handleSubmit,
    analyzeDocuments,
    isAnalyzed,
    hydrateInitialReport
  };
};

export default useChatWithAI;
